// src/components/ServiceRequestForm.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Button,
  Tooltip,
  IconButton,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  Autocomplete,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import BuildIcon from "@mui/icons-material/Build";
import { format, parseISO } from "date-fns";
import {
  DateField,
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

const filterServiceOptions = createFilterOptions({
  stringify: (option) => option?.service_name || "",
  ignoreCase: true,
  matchFrom: "any",
  trim: true,
});

const ServiceRequestForm = ({
  cars,
  selectedCarId,
  setSelectedCarId,
  services,
  selectedServices,
  setSelectedServices,
  selectedAutoshop,
  setSelectedAutoshop,
  newRadius,
  setNewRadius,
  autoshops,
  serviceDescription,
  setServiceDescription,
  availableDates,
  setAvailableDates,
  submitServiceRequest,
  isSubmittingServiceRequest,  // NEW: Loading state to prevent duplicate submissions
  onSuccess,
  userZip,
  setUserZip,
  zipCode,  // User's address ZIP code for comparison
  onZipSubmit,
  latitude,
  longitude,
  loadingShops,  // Loading state for shops
  requestType,  // NEW
  setRequestType,  // NEW
  availableRequestTypes,  // NEW
  requestTypesLoaded,  // NEW
  dropoffWeekStart = null,   // Monday of selected week (YYYY-MM-DD)
  setDropoffWeekStart,
  dropoffAvailableDays = [], // e.g. ['Mon','Tue','Wed','Thu'] for that week
  setDropoffAvailableDays,
}) => {
  const navigate = useNavigate();
  const [serviceFilterText, setServiceFilterText] = useState("");
  const [selectedDate, setSelectedDate] = useState(null); // a Dayjs object
  const serviceOptions = useMemo(
    () =>
      [...(services || [])].sort((a, b) =>
        String(a.service_name || "").localeCompare(
          String(b.service_name || ""),
          undefined,
          { sensitivity: "base" }
        )
      ),
    [services]
  );
  const selectedServiceOptions = serviceOptions.filter((service) =>
    selectedServices.includes(service.id)
  );
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const toggleDropoffDay = (day) => {
    if (!setDropoffAvailableDays) return;
    setDropoffAvailableDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };
  // Default dropoff week to current week's Monday if not set
  useEffect(() => {
    if (requestType === 'announcement' && setDropoffWeekStart && !dropoffWeekStart) {
      const today = dayjs();
      const monday = today.day() === 0 ? today.subtract(6, 'day') : today.subtract(today.day() - 1, 'day');
      setDropoffWeekStart(monday.format('YYYY-MM-DD'));
    }
  }, [requestType, setDropoffWeekStart, dropoffWeekStart]);
  const [startTime, setStartTime] = useState(null); // a Dayjs object
  const [endTime, setEndTime] = useState(null); // a Dayjs object
  const [zipSelected, setZipSelected] = useState(false); // Track if ZIP has been used
  const [lastUsedZip, setLastUsedZip] = useState(""); // Track the last ZIP that was used
  
  // Compute the display ZIP - use userZip if it has a value, otherwise use zipCode
  const displayZip = (userZip && userZip.trim() !== "") ? userZip : zipCode;
  
  // TASK-589: when profile zip changes, sync the Schedule form default ZIP
  // (do not leave a stale userZip from the previous profile address).
  useEffect(() => {
    if (zipCode && String(zipCode).trim().length >= 5) {
      setUserZip(zipCode);
      setZipSelected(true);
      setLastUsedZip(zipCode);
    }
  }, [zipCode]);
  
  // Reset zipSelected if user changes the ZIP after it was selected
  useEffect(() => {
    if (zipSelected && userZip !== lastUsedZip) {
      setZipSelected(false);
    }
  }, [userZip, zipSelected, lastUsedZip]);

  return (
    <Box>
      <Card
        sx={{
          p: 2,
          mb: 4,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
        }}
      >
        <CardContent>
          {/* REQUEST TYPE SELECTION - Only show if multiple types available */}
          {requestTypesLoaded && Object.values(availableRequestTypes).filter(Boolean).length > 1 && (
            <Box sx={{ mb: 4, p: 3, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: "1.1rem", mb: 2, color: "#000" }}>
                  How would you like to find a shop?
                </FormLabel>
                <RadioGroup
                  value={requestType}
                  onChange={(e) => {
                    setRequestType(e.target.value);
                    // Clear autoshop selection when switching to announcement/bidding
                    if (e.target.value !== 'direct') {
                      setSelectedAutoshop('');
                    }
                  }}
                >
                  {availableRequestTypes.direct && (
                    <FormControlLabel
                      value="direct"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            🎯 Request Quote from Specific Shop
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Select a shop below, they'll send you a quote to accept or decline
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                  {availableRequestTypes.announcement && (
                    <FormControlLabel
                      value="announcement"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            📢 Broadcast to Nearby Shops (Announcement)
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            All shops within your radius see your request, each submits ONE quote
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                  {availableRequestTypes.bidding && (
                    <FormControlLabel
                      value="bidding"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            💰 Competitive Bidding
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Shops compete with multiple bids to offer you the best price
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                </RadioGroup>
              </FormControl>
              
              {requestType === 'announcement' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>📢 Announcement System:</strong> Your request will be visible to all shops in your selected radius. Each shop can submit ONE final price. You choose which shop you prefer based on their quote and rating.
                  </Typography>
                </Alert>
              )}
              {requestType === 'bidding' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>💰 Bidding System:</strong> Shops can submit multiple competitive bids. You'll see all bids and can choose the best offer for your needs.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          <Grid container spacing={2} flexDirection="column">
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ fontSize: "1.1rem", mb: 3 }}
                variant="body1"
                align="center"
              >
                <strong>
                  Choose a specific Autoshop to request service directly — or
                  leave it open and let nearby shops compete for your business!
                </strong>
                <Tooltip
                  title={
                    <Typography sx={{ fontSize: "0.95rem", lineHeight: 1.4 }}>
                      If you select a specific Autoshop, you'll receive a fixed
                      price once they confirm your request. If you leave it
                      open, multiple in-network shops can bid to offer the best
                      price and availability.
                    </Typography>
                  }
                  arrow
                  placement="right"
                >
                  <IconButton size="small" sx={{ padding: 0, ml: 1 }}>
                    <InfoOutlineIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              </Typography>
            </Grid>

            {/* ZIP Code Entry for Location Services */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ fontSize: "1.1rem", mb: 1 }}
                variant="body1"
                color="text.secondary"
              >
                📍 Shops are shown near your profile address. To search elsewhere, enter an alternate ZIP:
              </Typography>
              <Box display="flex" gap={2}>
                <TextField
                  label="ZIP Code"
                  value={displayZip || ""}
                  onChange={(e) => setUserZip(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant={zipSelected ? "contained" : ((userZip || zipCode) && (userZip || zipCode) !== zipCode ? "contained" : "outlined")}
                  color={zipSelected ? "primary" : ((userZip || zipCode) && (userZip || zipCode) !== zipCode ? "warning" : "primary")}
                  onClick={async () => {
                    const zipToSubmit = userZip || zipCode || "";
                    await onZipSubmit(zipToSubmit);
                    // Mark ZIP as selected after successful submission
                    setZipSelected(true);
                    setLastUsedZip(zipToSubmit);
                  }}
                  disabled={(!userZip && !zipCode) || ((userZip || zipCode || "").length < 5)}
                  sx={{
                    ...(zipSelected && {
                      backgroundColor: "#1976d2",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                      },
                      fontWeight: 600,
                    }),
                    ...(!zipSelected && userZip && userZip !== zipCode && {
                      backgroundColor: "#ff9800",
                      "&:hover": {
                        backgroundColor: "#f57c00",
                      },
                      fontWeight: 600,
                    }),
                  }}
                >
                  {zipSelected ? "Zip Selected" : ((userZip || zipCode) && (userZip || zipCode) !== zipCode ? "⚠️ Use ZIP" : "Use ZIP")}
                </Button>
              </Box>
              {(userZip || zipCode) && (userZip || zipCode) !== zipCode && (
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, color: "#ff9800", display: "block" }}
                >
                  ⚠️ ZIP differs from your profile address. Click "Use alternate ZIP" to update the shop list.
                </Typography>
              )}
            </Grid>

            {/* Radius Selector and View Shops Nearby Button */}
            <Grid item xs={12} sm={6}>
              <Box display="flex" gap={2} alignItems="flex-end">
              <TextField
                select
                fullWidth
                label="Select Search Radius (miles)"
                value={newRadius}
                onChange={(e) => setNewRadius(e.target.value)}
                InputLabelProps={{
                  sx: { fontSize: "1.1rem" },
                }}
              >
                {[5, 10, 25, 50].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option} miles
                  </MenuItem>
                ))}
              </TextField>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    // Use the form ZIP code if available, otherwise use user's address ZIP
                    const zipToUse = (userZip && userZip.length === 5) ? userZip : (zipCode || "");
                    if (zipToUse) {
                      // Pass both ZIP and radius to the map page
                      navigate(`/google-wrapper?zip=${zipToUse}&radius=${newRadius}`);
                    } else {
                      navigate("/google-wrapper");
                    }
                  }}
                  sx={{ whiteSpace: "nowrap", minWidth: "fit-content", height: "56px" }}
                >
                  View Shops Near {displayZip || ""}
                </Button>
              </Box>
            </Grid>

            {/* Autoshop Dropdown - Only show for 'direct' request type */}
            {(!requestTypesLoaded || requestType === 'direct') && (
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label={requestType === 'direct' ? "Select Autoshop (Required)" : "Select Autoshop (Optional)"}
                value={selectedAutoshop}
                onChange={(e) => setSelectedAutoshop(e.target.value)}
                required={requestType === 'direct'}
                InputLabelProps={{
                  sx: { fontSize: "1.1rem" },
                }}
                SelectProps={{
                  native: false,
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        "& .MuiMenuItem-root:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      },
                    },
                  },
                }}
              >
                {/* Case: Loading shops */}
                {loadingShops && (
                  <MenuItem disabled>
                    <Typography sx={{ fontSize: "1.1rem" }}>
                      Loading shops...
                    </Typography>
                  </MenuItem>
                )}

                {/* Case: No in-network shops but user has location */}
                {!loadingShops && autoshops.length === 0 && latitude && longitude && (
                  <MenuItem disabled>
                    <Box sx={{ m: "10px" }} display="flex" flexDirection="column" width="100%">
                      <Typography sx={{ fontSize: "1.1rem", color: "#1a1a1a", fontWeight: 600 }}>
                        No in-network shops found near this zip code!
                      </Typography>
                      <Typography sx={{ fontSize: "0.9rem", color: "#444444", mt: 0.5 }}>
                        Try increasing the search radius or use the "View Shops Nearby" button to see all shops on the map.
                    </Typography>
                  </Box>
                  </MenuItem>
                )}

                {/* Case: No autoshops and no location */}
                {autoshops.length === 0 && !latitude && !longitude && (
                  <MenuItem disabled>
                    <Typography sx={{ fontSize: "1.1rem" }}>
                      Please enable location or enter ZIP code to see nearby
                      shops.
                    </Typography>
                  </MenuItem>
                )}

                {/* Show 'Let any autoshop reach out' only if in-network shops exist */}
                {/*autoshops.length > 0 && (
                  <MenuItem
                    value="Let any autoshop reach out"
                    sx={{
                      fontSize: "1.1rem",
                      borderRadius: 2,
                      boxShadow: 3,
                      backgroundColor: "#e0dcdcff",
                    }}
                  >
                    Let any autoshop reach out
                  </MenuItem>
                )*/}
                {/* Case: Show real autoshops */}
                {autoshops.length > 0 &&
                  autoshops.map((shop) => (
                    <MenuItem key={shop.id} value={shop.id}>
                      <Box display="flex" flexDirection="column" width="100%">
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography
                          sx={{ fontSize: "1.1rem" }}
                          fontWeight="bold"
                        >
                          {shop.shop_name}
                        </Typography>
                          {shop.distance_miles && (
                            <Typography
                              sx={{ fontSize: "0.9rem", color: "text.secondary", ml: 2 }}
                            >
                              📍 {shop.distance_miles} miles
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          sx={{ fontSize: "0.75rem", mt: 0.5 }}
                          variant="caption"
                          color="text.secondary"
                        >
                          {shop.address ||
                            `${shop.street_address}, ${shop.city}, ${shop.state} ${shop.zip_code}`}
                        </Typography>
                        {shop.rating && (
                          <Typography
                            sx={{ fontSize: "0.9rem", mt: 0.5 }}
                            variant="caption"
                            color="text.secondary"
                          >
                            ⭐ {shop.rating} |{" "}
                            {shop.in_network ? "In Network" : "Out of Network"}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            )}

            {/* Services: type to filter the full catalog, then check matches */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={serviceOptions}
                value={selectedServiceOptions}
                inputValue={serviceFilterText}
                onInputChange={(event, newValue, reason) => {
                  if (reason !== "reset") setServiceFilterText(newValue);
                }}
                onChange={(event, value) =>
                  setSelectedServices(value.map((service) => service.id))
                }
                filterOptions={filterServiceOptions}
                getOptionLabel={(option) => option.service_name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                slotProps={{
                  listbox: { sx: { maxHeight: 400, fontSize: "1.1rem" } },
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox checked={selected} />
                      <ListItemText primary={option.service_name} />
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Services"
                    placeholder="Type to filter"
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      sx: { fontSize: "1.1rem" },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Car Dropdown */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Select Your Car"
                value={selectedCarId}
                onChange={(e) => setSelectedCarId(e.target.value)}
              >
                {cars.length === 0 ? (
                  <MenuItem disabled>
                    No cars found. Please add one to your profile.
                  </MenuItem>
                ) : (
                  cars.map((car) => (
                    <MenuItem key={car.id} value={car.id}>
                      {`${car.year} ${car.carMake} ${car.carModel} ${
                        car.vin ? ` (${car.vin})` : ""
                      }`}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            {/* Description Field */}
            <Grid item xs={12}>
              <TextField
                label="Describe the issue or request"
                multiline
                fullWidth
                rows={3}
                value={serviceDescription}
                onChange={(e) => setServiceDescription(e.target.value)}
                sx={{ fontSize: "1.1rem" }}
              />
            </Grid>

            {/* Available Dates */}
            <Box>
              <Typography variant="h7" sx={{ mb: 2, display: "block" }}>
                <u>Click "Add Time Slot" to add up to 3 time slots</u>
              </Typography>
              <Grid
                container
                spacing={2}
                sx={{
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  mt: 1,
                }}
              >
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Preferred Date"
                      value={selectedDate}
                      onChange={(newValue) => setSelectedDate(newValue)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      label="Available From"
                      value={startTime}
                      onChange={(newValue) => setStartTime(newValue)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      label="Available Until"
                      value={endTime}
                      onChange={(newValue) => setEndTime(newValue)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Grid item xs={12} mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (
                      selectedDate &&
                      startTime &&
                      endTime &&
                      startTime.isValid() &&
                      endTime.isValid() &&
                      !endTime.isBefore(startTime)
                    ) {
                      // Combine date and start time to check if it's in the past
                      const startDateTime = selectedDate
                        .hour(startTime.hour())
                        .minute(startTime.minute())
                        .second(0);
                      
                      const now = dayjs();
                      const minimumAllowedTime = now.add(30, 'minute');

                      // Check if start time is in the past
                      if (startDateTime.isBefore(now)) {
                        alert("Cannot schedule a time slot in the past. Please select a future date and time.");
                        return;
                      }

                      // Check if start time is at least 30 minutes from now
                      if (startDateTime.isBefore(minimumAllowedTime)) {
                        alert("Time slot must be at least 30 minutes from now to give shops time to prepare.");
                        return;
                      }

                      // Check minimum time span (1 hour)
                      const durationInMinutes = endTime.diff(startTime, 'minute');
                      if (durationInMinutes < 60) {
                        alert("Time slot must be at least 1 hour long.");
                        return;
                      }

                      const formattedDate = selectedDate.format("YYYY-MM-DD");
                      const formattedStart = startTime.format("HH:mm");
                      const formattedEnd = endTime.format("HH:mm");

                      const isDuplicate = availableDates.some(
                        (d) =>
                          d.date === formattedDate &&
                          d.start_time === formattedStart &&
                          d.end_time === formattedEnd
                      );

                      if (!isDuplicate && availableDates.length < 3) {
                        setAvailableDates([
                          ...availableDates,
                          {
                            date: formattedDate,
                            start_time: formattedStart,
                            end_time: formattedEnd,
                          },
                        ]);
                        setSelectedDate(null);
                        setStartTime(null);
                        setEndTime(null);
                      }
                    }
                  }}
                  disabled={
                    !selectedDate ||
                    !startTime ||
                    !endTime ||
                    !startTime.isValid() ||
                    !endTime.isValid() ||
                    endTime.isBefore(startTime) ||
                    availableDates.length >= 3 ||
                    availableDates.some(
                      (d) =>
                        d.date === selectedDate?.format("YYYY-MM-DD") &&
                        d.start_time === startTime?.format("HH:mm") &&
                        d.end_time === endTime?.format("HH:mm")
                    )
                  }
                >
                  Add Time Slot
                </Button>
              </Grid>

              <Box mt={1}>
                {availableDates
                  .filter(
                    (date) => date.date && date.start_time && date.end_time
                  )
                  .map((date, i) => (
                    <Chip
                      key={i}
                      label={`${format(
                        parseISO(date.date),
                        "EEEE, MMMM d, yyyy"
                      )} (${format(
                        parseISO(`${date.date}T${date.start_time}`),
                        "h:mm a"
                      )} – ${format(
                        parseISO(`${date.date}T${date.end_time}`),
                        "h:mm a"
                      )})`}
                      onDelete={() =>
                        setAvailableDates((prev) =>
                          prev.filter((_, index) => index !== i)
                        )
                      }
                      sx={{ mr: 1, mt: 1 }}
                    />
                  ))}
              </Box>
            </Box>

            {/* Drop-off available days for a specific week (broadcast only) */}
            {requestType === 'announcement' && setDropoffAvailableDays && setDropoffWeekStart && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
                  I can also drop off on these days (for a specific week)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Optional. These are just opportunities you're presenting to shops — they'll respond with their own drop-off and pickup times based on their hours.
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Week of (Monday)"
                      value={dropoffWeekStart ? dayjs(dropoffWeekStart) : null}
                      onChange={(newValue) => {
                        if (newValue && setDropoffWeekStart) {
                          const monday = newValue.day() === 0 ? newValue.subtract(6, 'day') : newValue.subtract(newValue.day() - 1, 'day');
                          setDropoffWeekStart(monday.format('YYYY-MM-DD'));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" sx={{ maxWidth: 220 }} />
                      )}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {DAYS.map((day) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={dropoffAvailableDays.includes(day)}
                          onChange={() => toggleDropoffDay(day)}
                          size="small"
                        />
                      }
                      label={day}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Submit Button */}
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  await submitServiceRequest();
                  if (onSuccess) onSuccess();
                }}
                disabled={isSubmittingServiceRequest || false}
                sx={{ mt: 3, py: 2, width: "100%", maxWidth: "400px" }}
              >
                {isSubmittingServiceRequest ? "Submitting..." : "Submit Request"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ServiceRequestForm;
